import { ResourcesListItem } from "@app/resources/components/ResourcesListItem";
import { type Resource } from "@core/shared/types";
import { cn } from "@design-system/utils";
import { trans } from "@mongez/localization";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderOpenIcon,
} from "lucide-react";
import { useState } from "react";

type ResourcesSectionProps = {
  groupedResources: Record<string, Resource[]>;
  className?: string;
};

export function ResourcesList({
  groupedResources,
  className,
}: ResourcesSectionProps) {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const toggleSection = (category: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const categories = Object.keys(groupedResources);

  if (categories.length === 0) {
    return (
      <div className={cn("py-12 text-center", className)}>
        <div className="mx-auto max-w-md">
          <div className="mx-auto mb-6 w-fit rounded-full bg-gradient-to-br from-blue-50 to-indigo-100 p-6 dark:from-blue-900/20 dark:to-indigo-900/20">
            <svg
              className="h-12 w-12 text-blue-400 dark:text-blue-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-xl font-semibold text-gray-700 dark:text-gray-100">
            {trans("grades.noResourcesAvailable")}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            لا توجد موارد متاحة حالياً
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {categories.map((category, categoryIndex) => {
        const resources = groupedResources[category];
        const isExpanded =
          expandedSections[category] ??
          (categories.length === 1 && category === categories[0]);

        return (
          <section key={category} className="space-y-3">
            {/* Compact Section Header */}
            <div
              className={cn(
                "overflow-hidden rounded-2xl border transition-all duration-200",
                isExpanded
                  ? "border-primary/30 bg-primary/5 shadow-sm dark:border-primary/40 dark:bg-primary/10"
                  : "border-slate-200 bg-white hover:border-primary/20 dark:border-slate-800 dark:bg-slate-900",
              )}
            >
              <div className="p-4">
                <div className="flex items-center justify-between gap-3">
                  {/* Left Side - Title and Count */}
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 text-white shadow-md shadow-sky-200/70 dark:shadow-none">
                      <FolderOpenIcon className="h-5 w-5" />
                    </span>
                    <span className="text-sm font-black text-primary">
                      {String(categoryIndex + 1).padStart(2, "0")}
                    </span>
                    <h3
                      className={cn(
                        "truncate text-sm font-black transition-colors sm:text-base",
                        isExpanded
                          ? "text-blue-800 dark:text-blue-200"
                          : "text-gray-800 dark:text-gray-100",
                      )}
                    >
                      {category}
                    </h3>
                    <div
                      className={cn(
                        "flex-shrink-0 rounded-full px-3 py-1.5 shadow-sm transition-all duration-200",
                        isExpanded
                          ? "bg-gradient-to-r from-blue-600 to-indigo-600"
                          : "bg-gradient-to-r from-blue-500 to-indigo-500",
                      )}
                    >
                      <span className="text-xs font-semibold text-white">
                        {trans("grades.items", { count: resources.length })}
                      </span>
                    </div>
                  </div>

                  {/* Right Side - Action Button */}
                  <button
                    onClick={() => toggleSection(category)}
                    className={cn(
                      "group flex flex-shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-all duration-200 sm:text-sm",
                      isExpanded
                        ? "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-800/30 dark:text-blue-300 dark:hover:bg-blue-700/40"
                        : "bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-blue-900/20 dark:hover:text-blue-400",
                    )}
                  >
                    <span className="hidden sm:inline">
                      {isExpanded ? trans("hide") : trans("view")}
                    </span>
                    {isExpanded ? (
                      <ChevronDownIcon className="h-4 w-4 transition-transform group-hover:scale-110" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4 transition-transform group-hover:scale-110" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Resources Container - Expandable */}
            {isExpanded && (
              <div className="grid gap-3">
                {resources.map((resource, index) => (
                  <ResourcesListItem
                    key={resource.id || index}
                    resource={resource}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
